import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import Sparkline from './Sparkline';

interface DashboardHeroProps {
  /** OMTM label (e.g. "Leads en CRM"). */
  metricLabel: string;
  /** OMTM headline (the "big idea" — descriptive sentence). */
  headline: string;
  /** OMTM value (already formatted). */
  value: string;
  /** Colour for the OMTM value, for metrics whose value is a state rather than a quantity. */
  valueColor?: string;
  /** Optional context block below the value. Accepts any node so the parent can style highlights. */
  context?: ReactNode;
  /** Sparkline series for the OMTM. */
  sparkline?: number[];
  /** When set, a "view leads" icon appears next to the OMTM value and invokes this handler. */
  onNavigate?: () => void;
  /** Right-column slot — parent supplies the cells (e.g. a 2x2 Grid of cards / BulletGraphs). */
  supporting: ReactNode;
  /** Optional name of the segment / business unit. */
  segment?: string;
}

const DashboardHero: FC<DashboardHeroProps> = ({
  metricLabel,
  headline,
  value,
  valueColor = NAVY,
  context,
  sparkline,
  onNavigate,
  supporting,
  segment,
}) => {
  const NavigateButton: FC<{ onClick: () => void; ariaLabel: string; size?: 'sm' | 'lg' }> = ({
    onClick,
    ariaLabel,
    size = 'sm',
  }) => (
    <Tooltip title="Ver leads" arrow>
      <IconButton
        onClick={onClick}
        aria-label={ariaLabel}
        size="small"
        sx={{
          color: `${NAVY}80`,
          backgroundColor: 'transparent',
          '&:hover': { color: NAVY, backgroundColor: `${NAVY}0d` },
          width: size === 'lg' ? 40 : 30,
          height: size === 'lg' ? 40 : 30,
        }}
      >
        <ManageSearchIcon sx={{ fontSize: size === 'lg' ? 24 : 18 }} />
      </IconButton>
    </Tooltip>
  );
  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_COIN,
        boxShadow: SHADOW,
        overflow: 'hidden',
        my: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1.2fr' },
          gap: { xs: 3, lg: 4 },
          p: { xs: 3, md: 4, lg: 5 },
        }}
      >
        {/* Left — OMTM */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: `${NAVY}80`,
              mb: 1,
            }}
          >
            {segment ? `${segment} · ${metricLabel}` : metricLabel}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 3 } }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                lineHeight: 1.2,
                color: NAVY,
                maxWidth: 560,
                letterSpacing: '-0.01em',
              }}
            >
              {headline}
            </Typography>
            {onNavigate && <NavigateButton onClick={onNavigate} ariaLabel={`Ver leads de ${metricLabel}`} />}
          </Box>

          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 800,
              fontSize: { xs: '2.75rem', md: '3.5rem', lg: '4rem' },
              lineHeight: 1,
              color: valueColor,
              letterSpacing: '-0.03em',
            }}
          >
            {value}
          </Typography>

          {sparkline && sparkline.length > 1 && (
            <Box sx={{ mt: 2 }}>
              <Sparkline values={sparkline} width={220} height={44} />
            </Box>
          )}

          {context && (
            <Box
              sx={{
                mt: 2,
                fontFamily: 'Jost, sans-serif',
                fontSize: '1rem',
                color: `${NAVY}B3`,
                maxWidth: 520,
                lineHeight: 1.55,
              }}
            >
              {context}
            </Box>
          )}
        </Box>

        {/* Right — supporting slot supplied by parent (e.g. 2x2 grid of cards / BulletGraphs). */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_COIN,
            p: { xs: 2, md: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {supporting}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardHero;
